// Equal-gap "dynamic alignment" snap (Penpot-style distance channels) — pure AABB geometry, zoom enters only via `threshold`.

import { snapKindRank } from './overlay-transform';
import type { MoveSnapResult, SnapAxis, SnapGuide, SnapNeighbor, SnapNeighborKind, TransformRect } from './overlay-transform';

export interface GapSegment {
  /** Perpendicular-axis coordinate (book px) the dimension line is drawn at. */
  cross: number;
  /** Along-axis span (book px) the dimension line covers. */
  from: number;
  to: number;
}

export interface GapGuide {
  axis: SnapAxis;
  /** The equalized gap distance (book px), for the dimension-line label. */
  gap: number;
  /** One segment (rhythm match against a single neighbour) or two (sandwiched, equal on both sides). */
  segments: readonly GapSegment[];
}

export interface GapCandidate {
  axis: SnapAxis;
  /** Offset to add to the dragged rect's current position to realize this candidate. */
  delta: number;
  guide: GapGuide;
}

export interface EqualGapSnapResult {
  dx: number;
  dy: number;
  guides: GapGuide[];
}

function primaryRange(r: TransformRect, axis: SnapAxis): { start: number; end: number } {
  return axis === 'V' ? { start: r.x, end: r.x + r.width } : { start: r.y, end: r.y + r.height };
}

function crossRange(r: TransformRect, axis: SnapAxis): { start: number; end: number } {
  return axis === 'V' ? { start: r.y, end: r.y + r.height } : { start: r.x, end: r.x + r.width };
}

function overlapsCross(a: TransformRect, b: TransformRect, axis: SnapAxis): boolean {
  const ca = crossRange(a, axis);
  const cb = crossRange(b, axis);
  return ca.start < cb.end && cb.start < ca.end;
}

// Empty-space distance between two rects along `axis`; undefined when they overlap on it (no gap to speak of).
function gapBetween(a: TransformRect, b: TransformRect, axis: SnapAxis): number | undefined {
  const pa = primaryRange(a, axis);
  const pb = primaryRange(b, axis);
  if (pa.end <= pb.start) return pb.start - pa.end;
  if (pb.end <= pa.start) return pa.start - pb.end;
  return undefined;
}

function crossMid(a: TransformRect, b: TransformRect, axis: SnapAxis): number {
  const ca = crossRange(a, axis);
  const cb = crossRange(b, axis);
  return (Math.max(ca.start, cb.start) + Math.min(ca.end, cb.end)) / 2;
}

/** A distance the layout already uses, plus every pair that established it — the candidate draws the source
 *  ruler nearest to its own, so the match reads as "same as that group over there", not a far-off line. */
export interface GapChannel {
  gap: number;
  sources: GapSegment[];
}

// Gap values already established between neighbour pairs; zero-gap (flush) is excluded — align snap covers that.
// This is how a caption lands the same distance under its photo as the analogous group two elements away: the
// pair is not the dragged box's own neighbour, it is the rhythm it copies.
export function computeGapChannels(neighbors: readonly SnapNeighbor[], axis: SnapAxis): GapChannel[] {
  const found: GapChannel[] = [];
  for (let i = 0; i < neighbors.length; i++) {
    for (let j = i + 1; j < neighbors.length; j++) {
      const a = neighbors[i];
      const b = neighbors[j];
      if (!overlapsCross(a, b, axis)) continue;
      const gap = gapBetween(a, b, axis);
      if (gap === undefined || gap <= 0) continue;
      const pa = primaryRange(a, axis);
      const pb = primaryRange(b, axis);
      const [from, to] = pa.end <= pb.start ? [pa.end, pb.start] : [pb.end, pa.start];
      const segment = { cross: crossMid(a, b, axis), from, to };
      const channel = found.find(ch => ch.gap === gap);
      if (channel) channel.sources.push(segment);
      else found.push({ gap, sources: [segment] });
    }
  }

  return found.sort((x, y) => x.gap - y.gap);
}

export interface GapChannelsByAxis {
  V: readonly GapChannel[];
  H: readonly GapChannel[];
}

// Neighbours are frozen for the whole drag, so both axes' channels are computed once at drag-start, not per move.
export function computeGapChannelsByAxis(neighbors: readonly SnapNeighbor[]): GapChannelsByAxis {
  return { V: computeGapChannels(neighbors, 'V'), H: computeGapChannels(neighbors, 'H') };
}

interface Flank {
  rect: SnapNeighbor;
  gap: number;
}

/**
 * The neighbour the dragged box measures a gap against on one side — exactly ONE, its direct neighbour.
 * Class decides first (a frame outranks text for everyone; text is invisible to a frame), then proximity,
 * mirroring the align path's own pool (`nearestNeighbors`). Legacy hosts pass no class and keep the plain
 * nearest rect.
 */
function flankSlot(
  dragged: TransformRect,
  neighbors: readonly SnapNeighbor[],
  axis: SnapAxis,
  side: 'before' | 'after',
  draggedKind: SnapNeighborKind | undefined,
): Flank[] {
  let held: (Flank & { rank: number }) | undefined;
  const pd = primaryRange(dragged, axis);
  for (const n of neighbors) {
    if (!overlapsCross(dragged, n, axis)) continue;
    const rank = draggedKind ? snapKindRank(draggedKind, n.kind) : 0;
    if (rank === undefined) continue;
    const pn = primaryRange(n, axis);
    let gap: number;
    if (side === 'before' && pn.end <= pd.start) gap = pd.start - pn.end;
    else if (side === 'after' && pn.start >= pd.end) gap = pn.start - pd.end;
    else continue;
    if (!held || rank < held.rank || (rank === held.rank && gap < held.gap)) held = { rect: n, gap, rank };
  }

  return held ? [{ rect: held.rect, gap: held.gap }] : [];
}

// Both flanks present → position where gap(dragged,before) == gap(dragged,after); undefined if they'd overlap.
function sandwichedCandidate(dragged: TransformRect, before: Flank, after: Flank, axis: SnapAxis): GapCandidate | undefined {
  const pd = primaryRange(dragged, axis);
  const size = pd.end - pd.start;
  const beforeEnd = primaryRange(before.rect, axis).end;
  const afterStart = primaryRange(after.rect, axis).start;
  const gap = (afterStart - beforeEnd - size) / 2;
  if (gap < 0) return undefined;
  const newStart = beforeEnd + gap;
  const delta = newStart - pd.start;
  return {
    axis,
    delta,
    guide: {
      axis,
      gap,
      segments: [
        { cross: crossMid(dragged, before.rect, axis), from: beforeEnd, to: beforeEnd + gap },
        { cross: crossMid(dragged, after.rect, axis), from: afterStart - gap, to: afterStart },
      ],
    },
  };
}

function segmentMid(s: GapSegment): { along: number; cross: number } {
  return { along: (s.from + s.to) / 2, cross: s.cross };
}

// Among all pairs sharing a channel's gap value, the one whose ruler sits closest to the candidate's own —
// so the source drawn reads as "same as that group over there" instead of an arbitrary far-off pair.
function nearestSource(sources: readonly GapSegment[], own: GapSegment): GapSegment {
  const mid = segmentMid(own);
  let best = sources[0];
  let bestDist = Infinity;
  for (const s of sources) {
    const m = segmentMid(s);
    const d = Math.hypot(m.along - mid.along, m.cross - mid.cross);
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }

  return best;
}

// One flank present → candidate(s) reproducing a gap already used between two OTHER neighbours (rhythm match).
// The guide carries BOTH rulers: the one being built here and the nearest source pair's own, so the user sees
// which group the distance was copied from — the reference may sit two or three elements away.
function rhythmCandidates(
  dragged: TransformRect,
  flank: Flank,
  side: 'before' | 'after',
  channels: readonly GapChannel[],
  axis: SnapAxis,
  threshold: number,
): GapCandidate[] {
  const pd = primaryRange(dragged, axis);
  const pn = primaryRange(flank.rect, axis);
  const cross = crossMid(dragged, flank.rect, axis);
  const out: GapCandidate[] = [];
  for (const { gap, sources } of channels) {
    const delta = side === 'before' ? pn.end + gap - pd.start : pn.start - gap - pd.end;
    if (Math.abs(delta) >= threshold) continue;
    const segment = side === 'before' ? { cross, from: pn.end, to: pn.end + gap } : { cross, from: pn.start - gap, to: pn.start };
    out.push({ axis, delta, guide: { axis, gap, segments: [segment, nearestSource(sources, segment)] } });
  }

  return out;
}

/** All equal-gap candidates for one axis; not sorted — caller picks by `Math.abs(delta)`. `maxGap` drops
 *  candidates whose gap is wider than the cap (rulers across ~whole pages are noise, not rhythm).
 *  `draggedKind` puts the axis on the tiered rules: the distance is always measured to the direct neighbour of
 *  the best class, and only the rhythm match applies. Equalizing between the two flanks is dropped there —
 *  on a real spread those flanks routinely sit whole groups apart and the ruler measures empty space nobody
 *  laid out. */
export function computeEqualGapCandidates(
  dragged: TransformRect,
  neighbors: readonly SnapNeighbor[],
  axis: SnapAxis,
  threshold: number,
  channels?: readonly GapChannel[],
  maxGap = Infinity,
  draggedKind?: SnapNeighborKind,
): GapCandidate[] {
  const before = flankSlot(dragged, neighbors, axis, 'before', draggedKind);
  const after = flankSlot(dragged, neighbors, axis, 'after', draggedKind);
  const candidates: GapCandidate[] = [];
  if (!draggedKind) {
    for (const b of before) {
      for (const a of after) {
        const c = sandwichedCandidate(dragged, b, a, axis);
        if (c && Math.abs(c.delta) < threshold && c.guide.gap <= maxGap) candidates.push(c);
      }
    }
  }

  const ch = (channels ?? computeGapChannels(neighbors, axis)).filter(c => c.gap <= maxGap);
  for (const b of before) candidates.push(...rhythmCandidates(dragged, b, 'before', ch, axis, threshold));
  for (const a of after) candidates.push(...rhythmCandidates(dragged, a, 'after', ch, axis, threshold));

  return candidates;
}

function nearestCandidate(candidates: readonly GapCandidate[]): GapCandidate | undefined {
  let best: GapCandidate | undefined;
  for (const c of candidates) {
    if (!best || Math.abs(c.delta) < Math.abs(best.delta)) best = c;
  }

  return best;
}

/** Sticky per-axis hold, mirroring the align-snap hysteresis: the axis stays equalized until the raw
 *  position leaves the release radius. Identified by the equalized target coordinate, since candidate
 *  objects are rebuilt every frame. */
export interface GapAxisHold {
  target: number;
}

export interface GapSnapHold {
  v?: GapAxisHold;
  h?: GapAxisHold;
}

export interface GapSnapRadii {
  capture: number;
  release: number;
  /** Book px identity tolerance re-matching a held target among rebuilt candidates; 1 book px when unset. */
  holdEps?: number;
}

export interface GapMaxByAxis {
  V: number;
  H: number;
}

// One axis: a held target keeps winning while the raw position stays within `release` of it; only then
// does fresh capture (within `capture`) run on the nearest candidate.
function resolveGapAxis(
  dragged: TransformRect,
  neighbors: readonly SnapNeighbor[],
  axis: SnapAxis,
  radii: GapSnapRadii,
  channels: readonly GapChannel[] | undefined,
  held: GapAxisHold | undefined,
  maxGap: number,
  draggedKind: SnapNeighborKind | undefined,
): { candidate: GapCandidate; held: boolean } | undefined {
  const candidates = computeEqualGapCandidates(dragged, neighbors, axis, radii.release, channels, maxGap, draggedKind);
  const start = axis === 'V' ? dragged.x : dragged.y;
  if (held) {
    const kept = candidates.find(
      c => Math.abs(start + c.delta - held.target) < (radii.holdEps ?? 1) && Math.abs(c.delta) <= radii.release,
    );
    if (kept) return { candidate: kept, held: true };
  }

  const fresh = nearestCandidate(candidates.filter(c => Math.abs(c.delta) <= radii.capture));
  return fresh ? { candidate: fresh, held: false } : undefined;
}

/** Equal-gap snap for a move gesture: nearest candidate per axis, independent of the other axis.
 *  `heldAxes` tells the arbitration which axis is riding a hold rather than a fresh capture. */
export function computeEqualGapSnap(
  dragged: TransformRect,
  neighbors: readonly SnapNeighbor[],
  radii: GapSnapRadii,
  channels?: GapChannelsByAxis,
  hold: GapSnapHold = {},
  maxGap: GapMaxByAxis = { V: Infinity, H: Infinity },
  draggedKind?: SnapNeighborKind,
): EqualGapSnapResult & { hold: GapSnapHold; heldAxes: { V: boolean; H: boolean } } {
  const v = resolveGapAxis(dragged, neighbors, 'V', radii, channels?.V, hold.v, maxGap.V, draggedKind);
  const h = resolveGapAxis(dragged, neighbors, 'H', radii, channels?.H, hold.h, maxGap.H, draggedKind);
  const guides: GapGuide[] = [];
  if (v) guides.push(v.candidate.guide);
  if (h) guides.push(h.candidate.guide);

  return {
    dx: v?.candidate.delta ?? 0,
    dy: h?.candidate.delta ?? 0,
    guides,
    hold: {
      v: v ? { target: dragged.x + v.candidate.delta } : undefined,
      h: h ? { target: dragged.y + h.candidate.delta } : undefined,
    },
    heldAxes: { V: v?.held ?? false, H: h?.held ?? false },
  };
}

/** How exactly the losing magnet must agree with the winning position before it draws too (screen px;
 *  the tiered host scales it by radiusScale into the arbitration's `dualGuideEps`). */
export const DUAL_GUIDE_EPS_PX = 0.5;

export type SnapAxisOwner = 'align' | 'gap';

export interface CombinedMoveSnap {
  dx: number;
  dy: number;
  guides: SnapGuide[];
  gapGuides: GapGuide[];
  /** Which kind won each axis — the caller commits ONLY the winner's hold. */
  owner: { V?: SnapAxisOwner; H?: SnapAxisOwner };
}

/** Bounded magnitude competition between the two snap kinds, replacing hard align priority on tiered hosts. */
export interface MoveSnapArbitration {
  /** Last frame's winner per axis: the incumbent keeps the axis on a tie, so nothing flaps mid-hold. */
  owner: { V?: SnapAxisOwner; H?: SnapAxisOwner };
  /** Book px a challenger must beat the incumbent by before it takes the axis over. */
  margin: number;
  /** Book px within which the losing kind's guide draws alongside the winner (two confirmations). */
  dualGuideEps: number;
  /** Whether each axis' gap candidate is riding its hold — a held gap is defended, not re-contested. */
  gapHeld: { V: boolean; H: boolean };
}

// Priority vs. edge/centre align-snap. Legacy hosts (no `arb`) keep their old rule untouched: on a contested
// axis the smaller pull wins, align on a tie. Tiered hosts arbitrate with hysteresis instead — a caption's edge
// coincidence no longer kills a frame-frame ruler the user is visibly building. Hysteresis lives on BOTH sides: the axis
// stays with last frame's winner unless the challenger is closer by more than `margin`, and a HELD gap keeps
// its axis while its candidate survives (the resolvers only emit candidates inside their release radius).
export function combineMoveSnap(
  align: MoveSnapResult,
  gap: { dx: number; dy: number; guides: readonly GapGuide[] },
  arb?: MoveSnapArbitration,
): CombinedMoveSnap {
  const guides: SnapGuide[] = [];
  const gapGuides: GapGuide[] = [];
  const owner: { V?: SnapAxisOwner; H?: SnapAxisOwner } = {};
  let dx = 0;
  let dy = 0;

  (['V', 'H'] as const).forEach(axis => {
    const alignGuide = align.guides.find(g => g.axis === axis);
    const gapGuide = gap.guides.find(g => g.axis === axis);
    const alignDelta = axis === 'V' ? align.dx : align.dy;
    const gapDelta = axis === 'V' ? gap.dx : gap.dy;

    let win: SnapAxisOwner | undefined;
    if (alignGuide && gapGuide) {
      if (!arb) win = Math.abs(gapDelta) < Math.abs(alignDelta) ? 'gap' : 'align';
      else {
        const incumbent = arb.owner[axis] ?? (arb.gapHeld[axis] ? 'gap' : undefined);
        const alignPull = Math.abs(alignDelta);
        const gapPull = Math.abs(gapDelta);
        if (incumbent === 'gap') win = alignPull < gapPull - arb.margin ? 'align' : 'gap';
        else if (incumbent === 'align') win = gapPull < alignPull - arb.margin ? 'gap' : 'align';
        else win = gapPull < alignPull ? 'gap' : 'align';
      }
    } else if (alignGuide) win = 'align';
    else if (gapGuide) win = 'gap';

    if (win) owner[axis] = win;
    const delta = win === 'align' ? alignDelta : win === 'gap' ? gapDelta : 0;
    if (win === 'align' && alignGuide) guides.push(alignGuide);
    if (win === 'gap' && gapGuide) gapGuides.push(gapGuide);
    // The loser draws too when the winning position honours it as well — a tiered-host affordance;
    // the legacy album-editor host keeps its historical single-guide behaviour untouched.
    if (arb) {
      if (win === 'align' && gapGuide && Math.abs(gapDelta - delta) <= arb.dualGuideEps) gapGuides.push(gapGuide);
      if (win === 'gap' && alignGuide && Math.abs(alignDelta - delta) <= arb.dualGuideEps) guides.push(alignGuide);
    }

    if (axis === 'V') dx = delta;
    else dy = delta;
  });

  return { dx, dy, guides, gapGuides, owner };
}
